import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Store, ChevronRight, MapPin, Phone, Mail, Pencil, Trash2, ArrowLeft, User, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function RetailersPage() {
  const [retailers, setRetailers] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [stores, setStores] = useState([]);
  const [cities, setCities] = useState([]);
  // Dialogs
  const [retDlg, setRetDlg] = useState(false);
  const [editRet, setEditRet] = useState(null);
  const [retName, setRetName] = useState('');
  const [storeDlg, setStoreDlg] = useState(false);
  const [editStore, setEditStore] = useState(null);
  const emptyStore = { name: '', address: '', city: '', state: '', pin_code: '', contacts: [{ name: '', designation: '', phone: '', email: '', notes: '' }] };
  const [storeForm, setStoreForm] = useState({ ...emptyStore });

  const loadRetailers = useCallback(() => {
    api.get('/retailers', { params: { active_only: 'false' } }).then(r => setRetailers(r.data.retailers || []));
  }, []);

  const loadStores = useCallback(() => {
    if (!selectedRetailer) return;
    api.get(`/retailers/${selectedRetailer.retailer_id}/stores`).then(r => setStores(r.data.stores || []));
  }, [selectedRetailer]);

  useEffect(() => { loadRetailers(); api.get('/masters/cities').then(r => setCities(r.data.cities || [])); }, [loadRetailers]);
  useEffect(() => { loadStores(); }, [loadStores]);

  // ─── Retailer CRUD ───
  const saveRetailer = async () => {
    try {
      if (editRet) {
        await api.put(`/retailers/${editRet.retailer_id}`, { name: retName });
        toast.success('Updated');
      } else {
        await api.post('/retailers', { name: retName });
        toast.success('Retailer created');
      }
      setRetDlg(false); setEditRet(null); setRetName(''); loadRetailers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const delRetailer = async (id) => {
    if (!window.confirm('Delete this retailer?')) return;
    try {
      await api.delete(`/retailers/${id}`);
      toast.success('Deleted'); if (selectedRetailer?.retailer_id === id) setSelectedRetailer(null); loadRetailers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ─── Store CRUD ───
  const openCreateStore = () => { setEditStore(null); setStoreForm({ ...emptyStore }); setStoreDlg(true); };
  const openEditStore = (s) => {
    setEditStore(s);
    setStoreForm({ name: s.name || '', address: s.address || '', city: s.city || '', state: s.state || '', pin_code: s.pin_code || '', contacts: s.contacts?.length > 0 ? s.contacts : [{ name: '', designation: '', phone: '', email: '', notes: '' }] });
    setStoreDlg(true);
  };

  const saveStore = async () => {
    const rid = selectedRetailer.retailer_id;
    try {
      if (editStore) {
        await api.put(`/retailers/${rid}/stores/${editStore.store_id}`, storeForm);
        toast.success('Store updated');
      } else {
        await api.post(`/retailers/${rid}/stores`, storeForm);
        toast.success('Store created');
      }
      setStoreDlg(false); setEditStore(null); loadStores(); loadRetailers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const delStore = async (sid) => {
    if (!window.confirm('Delete this store?')) return;
    await api.delete(`/retailers/${selectedRetailer.retailer_id}/stores/${sid}`);
    toast.success('Deleted'); loadStores(); loadRetailers();
  };

  // ─── Contact helpers ───
  const updateContact = (idx, field, val) => {
    setStoreForm(f => {
      const contacts = [...f.contacts];
      contacts[idx] = { ...contacts[idx], [field]: val };
      return { ...f, contacts };
    });
  };
  const addContact = () => setStoreForm(f => ({ ...f, contacts: [...f.contacts, { name: '', designation: '', phone: '', email: '', notes: '' }] }));
  const removeContact = (idx) => setStoreForm(f => ({ ...f, contacts: f.contacts.filter((_, i) => i !== idx) }));

  return (
    <div data-testid="retailers-page" className="space-y-4">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Retailers & Stores</h1>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left: Retailer list */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{retailers.length} retailers</p>
            <Button size="sm" onClick={() => { setEditRet(null); setRetName(''); setRetDlg(true); }} data-testid="create-retailer-btn"><Plus size={14} className="mr-1" /> Add</Button>
          </div>
          <ScrollArea className="max-h-[calc(100vh-220px)]">
            <div className="space-y-1.5">
              {retailers.map(r => (
                <Card key={r.retailer_id} className={`border cursor-pointer transition-all hover:border-primary/40 ${selectedRetailer?.retailer_id === r.retailer_id ? 'border-primary bg-primary/5' : 'border-border'}`} data-testid={`retailer-card-${r.retailer_id}`}>
                  <CardContent className="p-3 flex items-center justify-between" onClick={() => setSelectedRetailer(r)}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Store size={16} className="text-primary shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{r.name}</p>
                        <p className="text-xs text-muted-foreground">{r.store_count || 0} stores</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={e => { e.stopPropagation(); setEditRet(r); setRetName(r.name); setRetDlg(true); }}><Pencil size={12} /></Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={e => { e.stopPropagation(); delRetailer(r.retailer_id); }}><Trash2 size={12} /></Button>
                      <ChevronRight size={14} className="text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              ))}
              {retailers.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No retailers. Click "Add" to create one.</p>}
            </div>
          </ScrollArea>
        </div>

        {/* Right: Stores panel */}
        <div className="lg:col-span-2">
          {!selectedRetailer ? (
            <Card className="border border-border"><CardContent className="p-12 text-center text-muted-foreground"><Building2 size={36} className="mx-auto mb-3 opacity-30" /><p>Select a retailer to manage its stores</p></CardContent></Card>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div><h2 className="font-heading text-lg font-semibold">{selectedRetailer.name}</h2><p className="text-xs text-muted-foreground">{stores.length} stores</p></div>
                <Button size="sm" onClick={openCreateStore} data-testid="create-store-btn"><Plus size={14} className="mr-1" /> Add Store</Button>
              </div>
              {stores.length === 0 ? <Card className="border border-dashed"><CardContent className="p-8 text-center text-muted-foreground">No stores yet. Add the first store for {selectedRetailer.name}.</CardContent></Card> : (
                <div className="space-y-2">
                  {stores.map(s => (
                    <Card key={s.store_id} className="border border-border" data-testid={`store-card-${s.store_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1.5 min-w-0 flex-1">
                            <p className="font-medium">{s.name}</p>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                              {s.city && <span><MapPin size={11} className="inline mr-0.5" />{s.city}{s.state ? `, ${s.state}` : ''}</span>}
                              {s.pin_code && <span>PIN: {s.pin_code}</span>}
                              {s.address && <span>{s.address}</span>}
                            </div>
                            {s.contacts?.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {s.contacts.map((c, i) => (
                                  <div key={i} className="text-xs flex flex-wrap gap-x-3 items-center bg-muted/50 rounded px-2 py-1">
                                    <span className="font-medium"><User size={10} className="inline mr-0.5" />{c.name || 'Contact'}</span>
                                    {c.designation && <span className="text-muted-foreground">{c.designation}</span>}
                                    {c.phone && <span><Phone size={10} className="inline mr-0.5" />{c.phone}</span>}
                                    {c.email && <span><Mail size={10} className="inline mr-0.5" />{c.email}</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1 ml-2 shrink-0">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditStore(s)} data-testid={`edit-store-${s.store_id}`}><Pencil size={13} /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => delStore(s.store_id)}><Trash2 size={13} /></Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ─── Retailer Dialog ─── */}
      <Dialog open={retDlg} onOpenChange={setRetDlg}>
        <DialogContent className="sm:max-w-md" data-testid="retailer-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editRet ? 'Edit' : 'Add'} Retailer</DialogTitle></DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Retailer Name *</Label>
            <Input value={retName} onChange={e => setRetName(e.target.value)} placeholder="e.g. Croma, Reliance Digital" data-testid="retailer-name-input" onKeyDown={e => e.key === 'Enter' && retName.trim() && saveRetailer()} />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setRetDlg(false)}>Cancel</Button><Button onClick={saveRetailer} disabled={!retName.trim()} data-testid="retailer-save-btn">{editRet ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Store Dialog ─── */}
      <Dialog open={storeDlg} onOpenChange={v => { if (!v) setEditStore(null); setStoreDlg(v); }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="store-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editStore ? 'Edit' : 'New'} Store — {selectedRetailer?.name}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="space-y-1.5"><Label>Store Name *</Label><Input value={storeForm.name} onChange={e => setStoreForm(f => ({ ...f, name: e.target.value }))} data-testid="store-name-input" /></div>
            <div className="space-y-1.5"><Label>Address</Label><Input value={storeForm.address} onChange={e => setStoreForm(f => ({ ...f, address: e.target.value }))} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>City</Label>
                <Select value={storeForm.city || 'none'} onValueChange={v => { const city = cities.find(c => c.city === v); setStoreForm(f => ({ ...f, city: v === 'none' ? '' : v, state: city?.state || f.state })); }}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{cities.map(c => <SelectItem key={c.city} value={c.city}>{c.city} ({c.state})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>State</Label><Input value={storeForm.state} onChange={e => setStoreForm(f => ({ ...f, state: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>PIN Code</Label><Input value={storeForm.pin_code} onChange={e => setStoreForm(f => ({ ...f, pin_code: e.target.value }))} placeholder="400001" /></div>
            </div>

            {/* Contacts */}
            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label>Contact Persons</Label><Button type="button" size="sm" variant="outline" onClick={addContact} data-testid="add-contact-btn"><Plus size={12} className="mr-1" /> Add Contact</Button></div>
              {storeForm.contacts.map((c, idx) => (
                <div key={idx} className="p-3 rounded-md border border-border space-y-2" data-testid={`contact-block-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Contact #{idx + 1}</span>
                    {storeForm.contacts.length > 1 && <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeContact(idx)}><Trash2 size={11} /></Button>}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Name" value={c.name} onChange={e => updateContact(idx, 'name', e.target.value)} className="text-sm" data-testid={`contact-name-${idx}`} />
                    <Input placeholder="Designation" value={c.designation} onChange={e => updateContact(idx, 'designation', e.target.value)} className="text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Phone" value={c.phone} onChange={e => updateContact(idx, 'phone', e.target.value)} className="text-sm" data-testid={`contact-phone-${idx}`} />
                    <Input placeholder="Email" value={c.email} onChange={e => updateContact(idx, 'email', e.target.value)} className="text-sm" />
                  </div>
                  <Input placeholder="Notes" value={c.notes} onChange={e => updateContact(idx, 'notes', e.target.value)} className="text-sm" />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setStoreDlg(false)}>Cancel</Button><Button onClick={saveStore} disabled={!storeForm.name.trim()} data-testid="store-save-btn">{editStore ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
